import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, useParams } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { SponsorShell } from "@/modules/sponsorPortal/components/SponsorShell";
import { SponsorEmptyState } from "@/modules/sponsorPortal/components/SponsorPrimitives";
import { sponsorSessionQuery } from "@/modules/sponsorPortal/sponsorPortal.queries";
import type {
  SponsorPortalSponsor,
  SponsorSession,
} from "@/modules/sponsorPortal/sponsorPortal.types";

export type SponsorOutletContext = {
  sponsor: SponsorPortalSponsor;
  sponsors: SponsorPortalSponsor[];
};

const SponsorPortalSpinner = () => (
  <div className="flex min-h-screen items-center justify-center bg-background-two dark:bg-neutral-950">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-color border-b-transparent" />
  </div>
);

/**
 * Guards every sponsor route and supplies the selected sponsor to its children.
 *
 * Authority comes from `GET /sponsor/auth/session`, which reads only the `UserId` claim. A patient
 * token carries that same claim, so a sponsor already signed in on the patient side lands straight
 * here with no second sign-in — and a visitor with no sponsor administrations gets the same 403 as
 * one with no token at all, which is what sends both to the sign-in page.
 */
export const SponsorLayout = () => {
  const { t } = useTranslation();
  const { platformSponsorId } = useParams();
  const { data, isLoading, isError } = useQuery(sponsorSessionQuery());

  if (isLoading) return <SponsorPortalSpinner />;

  // Any failure here — no token, an expired one, or a user who administers no sponsor — resolves
  // to the same place. Distinguishing them would tell an unauthenticated visitor which phone
  // numbers administer a sponsor.
  if (isError) return <Navigate to="/sponsor/signin" replace />;

  const sponsors = (data as SponsorSession).sponsors;

  if (sponsors.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-two dark:bg-neutral-950">
        <SponsorEmptyState
          title={t("sponsorPortal.noSponsorsTitle")}
          body={t("sponsorPortal.noSponsorsBody")}
        />
      </div>
    );
  }

  // No sponsor in the URL: send the visitor to their first one. With a single sponsor — the common
  // case — this makes /sponsor a working entry point rather than a chooser with one option.
  if (!platformSponsorId) {
    return <Navigate to={`/sponsor/${sponsors[0].platformSponsorId}`} replace />;
  }

  const sponsor = sponsors.find((candidate) => candidate.platformSponsorId === platformSponsorId);

  // A sponsor id the caller does not administer is not an error page: they are signed in and do
  // have somewhere to be, so send them there.
  if (!sponsor) {
    return <Navigate to={`/sponsor/${sponsors[0].platformSponsorId}`} replace />;
  }

  return (
    <SponsorShell sponsor={sponsor} sponsors={sponsors}>
      <Outlet context={{ sponsor, sponsors } satisfies SponsorOutletContext} />
    </SponsorShell>
  );
};
